import React, { useEffect, useState } from "react";
import { Button, Form, Input, Select, Checkbox, message, Tooltip, Col, Row } from "antd";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { WhatsAppOutlined } from "@ant-design/icons";
import SidebarLogo from "./Sidebar/SidebarLogo";
import { countries } from "../constants/country";
import { fetchStart, fetchSuccess, getPorts } from "../appRedux/actions/Common";
import { getBusinessTypeOption } from "../appRedux/actions/authAction";
import { labelComponent } from "../util/UtilComponents";
import { axiosInstance, BASE_API_CONFIG } from "../util/Api";
import { isArrayNonEmpty } from "../util/ApiHelper";
import {
  BUSINESS_TYPE,
  STATUS_BUSINESS_TYPE,
  USER_STATUS,
} from "../util/Constant";

const { Option } = Select;
const FormItem = Form.Item;

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
const GST_CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const MAX_LOADING_PORTS = 5;
const MAX_PORT_PAIRS = 5;

const normalizeEmail = (value = "") => value.trim().toLowerCase();
const normalizeMobile = (value = "") => String(value).replace(/\D/g, "");
const normalizeGst = (value = "") => value.trim().toUpperCase();

const isValidGstChecksum = (gstNumber = "") => {
  if (!GST_REGEX.test(gstNumber)) {
    return false;
  }

  let factor = 2;
  let sum = 0;

  for (let index = gstNumber.length - 2; index >= 0; index -= 1) {
    const codePoint = GST_CHARSET.indexOf(gstNumber[index]);
    if (codePoint < 0) {
      return false;
    }

    const digitSum = codePoint * factor;
    sum += Math.floor(digitSum / 36) + (digitSum % 36);
    factor = factor === 2 ? 1 : 2;
  }

  const checksumIndex = (36 - (sum % 36)) % 36;
  return GST_CHARSET[checksumIndex] === gstNumber[gstNumber.length - 1];
};

const getDuplicateFieldErrors = (apiMessage) => {
  const lowerMessage = (apiMessage || "").toLowerCase();
  const fieldErrors = [];

  if (lowerMessage.includes("email")) {
    fieldErrors.push({
      name: "email",
      errors: ["This email is already registered. Please log in or use another email."],
    });
  }

  if (
    lowerMessage.includes("mobile") ||
    lowerMessage.includes("phone") ||
    lowerMessage.includes("whatsapp")
  ) {
    fieldErrors.push({
      name: "mobile",
      errors: ["This mobile number is already registered. Please log in or use another number."],
    });
  }

  if (lowerMessage.includes("gst")) {
    fieldErrors.push({
      name: "gstNumber",
      errors: ["This GST number is already registered. Please verify it before continuing."],
    });
  }

  return fieldErrors;
};

const SignUp = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();

  const loading = useSelector(({ common }) => common.loading);
  const businessTypeOptions = useSelector(({ auth }) => auth.businessTypes);
  const ports = useSelector(({ common }) => common.ports);

  const [selectedCountry, setSelectedCountry] = useState("+00");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [portsList, setPortsList] = useState([]);
  const [indianPorts, setIndianPorts] = useState([]);
  const [selectedLoadingPorts, setSelectedLoadingPorts] = useState([]);
  const [selectedLoadingPort, setSelectedLoadingPort] = useState(null);
  const [selectedDestinationPort, setSelectedDestinationPort] = useState(null);
  const [portPairs, setPortPairs] = useState([]);
  const [referrerId, setReferrerId] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const businessTypeId = Form.useWatch("businessType", form);
  const selectedBusinessTypeOption = businessTypeOptions?.find(
    (item) => Number(item?.id) === Number(businessTypeId)
  );
  const businessTypeCode = selectedBusinessTypeOption?.code;
  console.log("Selected Business Type Code:", businessTypeCode);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get("email");
    const role = params.get("role");
    const referrer = params.get("referrer");

    if (email) {
      form.setFieldsValue({ email: normalizeEmail(decodeURIComponent(email)) });
    }

    if (referrer) {
      setReferrerId(referrer);
    }

    if (role === "freight_forwarder" && Array.isArray(businessTypeOptions)) {
      const freightForwarderOption = businessTypeOptions.find((item) =>
        item.name?.toLowerCase().includes("freight forwarder")
      );

      if (freightForwarderOption) {
        form.setFieldsValue({ businessType: freightForwarderOption.id });
      }
    }
  }, [location.search, form, businessTypeOptions]);

  useEffect(() => {
    form.setFieldsValue({ portPairs });
  }, [portPairs, form]);

  useEffect(() => {
    const script = document.createElement("script");
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-WZ3NGKQQ');
    `;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    dispatch(getBusinessTypeOption());
    dispatch(getPorts());
  }, [dispatch]);

  useEffect(() => {
    if (isArrayNonEmpty(ports)) {
      const onlyIndianPorts = Array.from(
        new Map(
          ports
            .filter((item) => item?.countryId === 105)
            .map((item) => [item.port, item])
        ).values()
      );
      setIndianPorts(onlyIndianPorts);
    }
  }, [ports]);

  useEffect(() => {
    if (isArrayNonEmpty(ports)) {
      const onlyDestinationPorts = Array.from(
        new Map(
          ports
            .filter((item) => item?.countryId !== 105)
            .map((item) => [item.port, item])
        ).values()
      );
      setPortsList(onlyDestinationPorts);
    }
  }, [ports]);

  const handleLoadingPortSelect = (value) => {
    if (selectedLoadingPorts.length >= MAX_LOADING_PORTS) {
      message.warning(`You can select a maximum of ${MAX_LOADING_PORTS} loading ports!`);
      return;
    }

    const updatedPorts = [...selectedLoadingPorts, value];
    setSelectedLoadingPorts(updatedPorts);
    form.setFieldsValue({ loadingPort: updatedPorts });
  };

  const handleLoadingPortDeselect = (value) => {
    setSelectedLoadingPorts((prevPorts) => {
      const updatedPorts = prevPorts.filter((port) => port !== value);
      form.setFieldsValue({ loadingPort: updatedPorts });
      return updatedPorts;
    });
  };

  const addPortPair = () => {
    if (!selectedLoadingPort || !selectedDestinationPort) {
      message.warning("Please select both loading and destination ports!");
      return;
    }

    const existingPair = portPairs.find(
      (pair) =>
        pair.loadingPortId === selectedLoadingPort &&
        pair.destinationPortId === selectedDestinationPort
    );

    if (existingPair) {
      message.warning("This port pair already exists!");
      return;
    }

    if (portPairs.length >= MAX_PORT_PAIRS) {
      message.warning(`You can add maximum ${MAX_PORT_PAIRS} port pairs!`);
      return;
    }

    const updatedPairs = [
      ...portPairs,
      {
        id: Date.now(),
        loadingPortId: selectedLoadingPort,
        destinationPortId: selectedDestinationPort,
      },
    ];

    setPortPairs(updatedPairs);
    form.setFieldsValue({ portPairs: updatedPairs });
    setSelectedLoadingPort(null);
    setSelectedDestinationPort(null);
    message.success("Port pair added successfully!");
  };

  const removePortPair = (pairId) => {
    setPortPairs((prevPairs) => prevPairs.filter((pair) => pair.id !== pairId));
    message.success("Port pair removed!");
  };

  const onFinishFailed = () => {};



   const checkUserExists = async (email, mobile, gst) => {
    try {
      const response = await axiosInstance.get(`${BASE_API_CONFIG.baseURL}/auth/check-user`, {
        params: { email, mobile, gst }
      });
      return response?.data;
    } catch (error) {
      console.error("Check user error:", error);
      return null;
    }
  };

const onFinish = async (values) => {
  // Prevent double submission
  if (isSubmitting) {
    return;
  }
  
  setIsSubmitting(true);

  const selectedBusinessTypeId = Number(values?.businessType);
  const selectedBusinessType = businessTypeOptions?.find(
    (item) => Number(item?.id) === selectedBusinessTypeId
  );
  const businessTypeId = selectedBusinessType?.id;

  if (!businessTypeId) {
    form.setFields([
      {
        name: "businessType",
        errors: ["Please select a valid nature of business."],
      },
    ]);
    setIsSubmitting(false);
    return;
  }

  const normalizedEmail = normalizeEmail(values.email);
  const normalizedMobile = normalizeMobile(values.mobile);
  const normalizedGst = normalizeGst(values.gstNumber);
  const fullMobile = `${selectedCountry?.dial_code || ""}${normalizedMobile}`;

  form.setFieldsValue({
    email: normalizedEmail,
    mobile: normalizedMobile,
    gstNumber: normalizedGst,
  });

  const loadingPort = ports
    .filter((port) => values?.loadingPort?.includes(port?.id))
    .map((port) => `${port.port}(${port.code})`);

  const formattedPortPairs = portPairs.map((pair) => {
    const loadingPortItem = indianPorts.find((item) => item.id === pair.loadingPortId);
    const destinationPortItem = portsList.find((item) => item.id === pair.destinationPortId);
    return `${loadingPortItem?.port}(${loadingPortItem?.code}) -> ${destinationPortItem?.port}(${destinationPortItem?.code})`;
  });

  const allowedStatusTypes = [
    STATUS_BUSINESS_TYPE.CUSTOM_HOUSE_AGENT,
    STATUS_BUSINESS_TYPE.FREIGHT_FORWARDER,
    STATUS_BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER,
  ];

  const payload = {
    name: values?.name?.trim(),
    email: normalizedEmail,
    companyName: values?.company?.trim(),
    mobile: fullMobile,
    businessTypeId,
    country: selectedCountry?.name,
    countryCode: selectedCountry?.code,
    gstNumber: normalizedGst || undefined,
    loadingPort,
    port: [],
    portPairs: formattedPortPairs,
    ...(allowedStatusTypes.includes(businessTypeId) ? { status: USER_STATUS.PENDING } : {}),
    ...(referrerId ? { referrer: referrerId } : {}),
  };

  dispatch(fetchStart());

  try {
    const createResponse = await axiosInstance.post(`${BASE_API_CONFIG.baseURL}/users`, payload, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    console.log("📥 Response:", createResponse?.data);

    // ✅ Success - New user created (Status 201)
    if (createResponse?.status === 201 || createResponse?.data?.data) {
      await axiosInstance.get(`${BASE_API_CONFIG.baseURL}/auth/whatsapp/otp?mobile=${fullMobile}`);
      history.push(`otpverify?mobile=${fullMobile}&email=${normalizedEmail}`);
    } else {
      message.error("Registration failed. Please try again.");
      setIsSubmitting(false);
    }
    
  } catch (error) {
    console.error("Registration error:", error);
    
    // ✅ Handle 409 Conflict - DUPLICATE USER
    if (error?.response?.status === 409) {
      const apiMessage = error?.response?.data?.message || "This account already exists. Please login instead.";
      const field = error?.response?.data?.field;
      
      if (field === 'email') {
        message.error("This email is already registered. Please login instead.");
        form.setFields([{ name: "email", errors: ["Email already registered"] }]);
      } else if (field === 'mobile') {
        message.error("This mobile number is already registered. Please login instead.");
        form.setFields([{ name: "mobile", errors: ["Mobile already registered"] }]);
      } else if (field === 'gst') {
        message.error("This GST number is already registered. Please login instead.");
        form.setFields([{ name: "gstNumber", errors: ["GST already registered"] }]);
      } else {
        message.error(apiMessage);
      }
    } else {
      // Handle other errors
      const errorMessage = error?.response?.data?.message || "Registration failed. Please try again.";
      message.error(errorMessage);
    }
    
    setIsSubmitting(false);
    
  } finally {
    dispatch(fetchSuccess());
  }
};

  const isGstApplicable =
    businessTypeCode === BUSINESS_TYPE.EXPORTER ||
    businessTypeCode === BUSINESS_TYPE.MANUFACTURER ||
    businessTypeCode === BUSINESS_TYPE.MANUFACTURER_EXPORTER ||
    businessTypeCode === BUSINESS_TYPE.CUSTOM_HOUSE_AGENT ||
    businessTypeCode === BUSINESS_TYPE.COMMISSION_AGENT ||
    businessTypeCode === BUSINESS_TYPE.FREIGHT_FORWARDER ||
    businessTypeCode === BUSINESS_TYPE.SHIPPING_LINE ||
    businessTypeCode === BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER;

  const isHandlePortApplicable =
    businessTypeCode === BUSINESS_TYPE.EXPORTER ||
    businessTypeCode === BUSINESS_TYPE.MANUFACTURER_EXPORTER;

  const isHandlePortApplicableCHA =
    businessTypeCode === BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER ||
    businessTypeCode === BUSINESS_TYPE.CUSTOM_HOUSE_AGENT ||
    businessTypeCode === BUSINESS_TYPE.FREIGHT_FORWARDER;

  return (
    <>
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-WZ3NGKQQ"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="google-tag-manager"
        />
      </noscript>

      <div className="gx-login-container" style={{ marginTop: "auto", paddingTop: 0 }}>
        <div style={{ width: "100%", paddingBottom: "40px" }}>
          <SidebarLogo />
        </div>

        <div className="gx-login-content">
          <div className="gx-mb-4">
            <div className="gx-login-header">Create Account</div>
            <p className="gx-login-description">
              Already have an account? &nbsp;
              <Link className="myexim-link" to="/signin">
                Login
              </Link>
            </p>
          </div>

          <Form
            form={form}
            initialValues={{ remember: true, portPairs: [] }}
            name="signup"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <FormItem
              name="name"
              rules={[{ required: true, message: "Please input your Name!" }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent("Name", null)}
            >
              <Input maxLength={75} size="large" placeholder="Name" />
            </FormItem>

            <FormItem
              name="company"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent("Company Name", null)}
              rules={[{ required: true, message: "Please input your Company Name!" }]}
            >
              <Input maxLength={75} size="large" placeholder="Company" />
            </FormItem>

            <FormItem
              name="email"
              rules={[
                { required: true, message: "Please input your E-mail!" },
                { type: "email", message: "The input is not a valid E-mail!" },
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent(
                "Email",
                "Please note that this email ID will be used for logging in to My Exim Business."
              )}
            >
              <Input
                maxLength={75}
                size="large"
                placeholder="Email"
                type="email"
                onBlur={(event) => {
                  form.setFieldsValue({ email: normalizeEmail(event.target.value) });
                }}
              />
            </FormItem>

            <FormItem
              name="country"
              rules={[{ required: true, message: "Please input your Country!" }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent("Country", null)}
            >
              <Select
                showSearch
                size="large"
                name="country"
                placeholder="Country"
                onChange={(value) => {
                  const country = countries.find((item) => item?.name === value);
                  setSelectedCountry(country);
                  setSelectedLoadingPorts([]);
                  form.resetFields(["businessType", "mobile", "loadingPort"]);
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {countries?.map((country) => (
                  <Option key={country.code} value={country.name}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              name="mobile"
              placeholder="WhatsApp Number"
              rules={[
                { required: true, message: "Please provide your WhatsApp-enabled mobile number!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const normalizedValue = normalizeMobile(value);
                    const country = getFieldValue("country");

                    if (country === "India") {
                      if (/^[6-9]\d{9}$/.test(normalizedValue)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "Please enter a valid 10-digit Indian WhatsApp number"
                      );
                    }

                    if (normalizedValue && normalizedValue.length >= 5) {
                      return Promise.resolve();
                    }

                    return Promise.reject("Please enter a valid WhatsApp number");
                  },
                }),
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent("Mobile Number")}
            >
              <Input
                maxLength={20}
                size="large"
                type="tel"
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key) && event.key !== "Backspace") {
                    event.preventDefault();
                  }
                }}
                onBlur={(event) => {
                  form.setFieldsValue({ mobile: normalizeMobile(event.target.value) });
                }}
                placeholder="WhatsApp-registered mobile number"
                prefix={
                  selectedCountry?.dial_code ? (
                    <div style={{ borderRight: "1px solid #d3d3d3", paddingRight: 5 }}>
                      {selectedCountry?.dial_code}
                    </div>
                  ) : (
                    ""
                  )
                }
                suffix={
                  <Tooltip title="Make sure this number is registered with WhatsApp">
                    <WhatsAppOutlined style={{ color: "#25D366", fontSize: "20px" }} />
                  </Tooltip>
                }
              />
            </FormItem>

            <FormItem
              hidden={selectedCountry === "+00"}
              name="businessType"
              rules={[
                {
                  required: true,
                  message: "Please input your Nature of Business!",
                },
              ]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              label={labelComponent("Nature Of Business", null)}
            >
              <Select showSearch size="large" name="businessType" placeholder="Nature of Business">
                {businessTypeOptions?.map((item) => {
                  if (
                    form.getFieldValue("country") == null ||
                    form.getFieldValue("country") === "India"
                  ) {
                    if (item?.id !== 1) {
                      return (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      );
                    }
                  } else if (item?.id === 1) {
                    return (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    );
                  }

                  return null;
                })}
              </Select>
            </FormItem>

            {isGstApplicable && (
              <FormItem
                name="gstNumber"
                rules={[
                  { required: true, message: "Please input your GST No" },
                  {
                    pattern:
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/,
                    message: "The input is not a valid GST Number",
                  },
                ]}
                name="gstNumber"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent("GST Number", null)}
              >
                <Input maxLength={15} size="large" placeholder="GST Number" />
              </FormItem>
            )}

            {isHandlePortApplicableCHA && (
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label={labelComponent(
                  businessTypeCode === BUSINESS_TYPE.FREIGHT_FORWARDER ||
                    businessTypeCode === BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER
                    ? "CHA Handling Port"
                    : "Loading Port",
                  businessTypeCode === BUSINESS_TYPE.FREIGHT_FORWARDER ||
                    businessTypeCode === BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER
                    ? "Select the Handling Port from India where the goods will be processed"
                    : "Select the Loading Port where Goods to be loaded"
                )}
                name="loadingPort"
                rules={[
                  {
                    required: true,
                    message:
                      businessTypeCode === BUSINESS_TYPE.FREIGHT_FORWARDER ||
                      businessTypeCode === BUSINESS_TYPE.CUSTOMHOUSEAGENTS_FREIGHTFORWARDER
                        ? "Handling Port is required"
                        : "Loading Port is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  mode="multiple"
                  virtual={false}
                  size="large"
                  onSelect={handleLoadingPortSelect}
                  onDeselect={handleLoadingPortDeselect}
                  maxTagCount={MAX_LOADING_PORTS}
                  placeholder="Select Loading Port"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {indianPorts?.map((option) => (
                    <Option
                      key={option.id}
                      value={option.id}
                      disabled={
                        selectedLoadingPorts.length === MAX_LOADING_PORTS &&
                        !selectedLoadingPorts.includes(option.id)
                      }
                    >
                      {`${option.port} (${option.code})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {isHandlePortApplicable && (
              <div style={{ marginTop: -20, marginBottom: 10 }}>
                <FormItem
                  name="portPairs"
                  rules={[
                    {
                      required: true,
                      validator: () => {
                        if (portPairs.length === 0) {
                          return Promise.reject(
                            "Please add at least one port pair for your business type!"
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input type="hidden" />
                </FormItem>

                <Row gutter={16}>
                  <Col span={12}>
                    <FormItem
                      style={{ marginTop: -30 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      label={labelComponent(<span>Loading Port</span>, null)}
                      required
                    >
                      <Select
                        showSearch
                        size="large"
                        value={selectedLoadingPort}
                        placeholder="Select Loading Port"
                        onChange={(value) => setSelectedLoadingPort(value)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        {indianPorts?.map((option) => (
                          <Option key={option.id} value={option.id}>
                            {`${option.port} (${option.code})`}
                          </Option>
                        ))}
                      </Select>
                    </FormItem>
                  </Col>

                  <Col span={12}>
                    <FormItem
                      style={{ marginTop: -30 }}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      label={labelComponent(<span>Destination Port</span>, null)}
                      required
                    >
                      <Select
                        showSearch
                        size="large"
                        value={selectedDestinationPort}
                        placeholder="Select Destination Port"
                        onChange={(value) => setSelectedDestinationPort(value)}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        {portsList?.map((item) => (
                          <Option key={item.id} value={item.id}>
                            {`${item.port} (${item.code}) -> ${item.countryName}`}
                          </Option>
                        ))}
                      </Select>
                    </FormItem>
                  </Col>
                </Row>

                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <Button
                    type="primary"
                    onClick={addPortPair}
                    disabled={
                      !selectedLoadingPort ||
                      !selectedDestinationPort ||
                      portPairs.length >= MAX_PORT_PAIRS
                    }
                  >
                    Add Port Pair
                  </Button>
                  <span style={{ marginLeft: 8, fontSize: "12px", color: "#666" }}>
                    ({portPairs.length}/{MAX_PORT_PAIRS} pairs added)
                  </span>
                </div>

                {portPairs.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      padding: "12px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <h4 style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
                      Selected Port Pairs:
                    </h4>
                    {portPairs.map((pair) => {
                      const loadingPortItem = indianPorts.find(
                        (item) => item.id === pair.loadingPortId
                      );
                      const destinationPortItem = portsList.find(
                        (item) => item.id === pair.destinationPortId
                      );

                      return (
                        <div
                          key={pair.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            marginBottom: "8px",
                            backgroundColor: "#fff",
                            border: "1px solid #e8e8e8",
                            borderRadius: "4px",
                          }}
                        >
                          <span style={{ fontSize: "14px" }}>
                            <strong>{`${loadingPortItem?.port} (${loadingPortItem?.code})`}</strong>
                            {" -> "}
                            <strong>{`${destinationPortItem?.port} (${destinationPortItem?.code})`}</strong>
                          </span>
                          <Button type="text" size="small" danger onClick={() => removePortPair(pair.id)}>
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <FormItem
              name="agreedToTerms"
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              rules={[
                {
                  required: true,
                  validator: () => {
                    if (agreedToTerms) {
                      return Promise.resolve();
                    }

                    return Promise.reject("Please agree to the Terms and Conditions.");
                  },
                },
              ]}
            >
              <Checkbox checked={agreedToTerms} onChange={(event) => setAgreedToTerms(event.target.checked)}>
                I agree to my exim business&nbsp;
                <a href="/terms-of-service" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>
                &nbsp;and&nbsp;
                <a href="/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              </Checkbox>
            </FormItem>

            <FormItem className="gx-text-center" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
              <Button type="primary" size="large" htmlType="submit" block loading={loading}>
                Register Account
              </Button>
            </FormItem>
          </Form>
        </div>
      </div>
    </>
  );
};

export default SignUp;
